import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AddCircle as AddCircleIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import {
  RuleType,
  CustomerRules,
  PreviewTimeframe,
  LeadStatus,
  UnbekannteDistribution,
  DistributionEntry,
  customerNames,
  timeframeLabels,
  leadStatusLabels,
} from '../../types/leadSourceRules';
import { postData } from '../../helpers/apiUtils';

const columnDisplayNames: Record<string, string> = {
  origin_host: 'Origin Host',
  source: 'Source',
  clean_affiliate: 'Clean Affiliate',
  affiliate: 'Affiliate',
  blog: 'Blog',
  type: 'Type',
  netzwerk_name: 'Netzwerk',
  zielgruppe: 'Zielgruppe',
};

interface UnbekannteLeadsDialogProps {
  open: boolean;
  onClose: () => void;
  ruleType: RuleType;
  customer: string;
  rules: CustomerRules | null;
  existingSources: string[];
  onCreateRule: (column: string, value: string | null, targetSource: string) => void;
  onShowLeadPreview: (timeframe: PreviewTimeframe, leadStatus: LeadStatus) => void;
}

interface CreateRuleState {
  open: boolean;
  column: string;
  value: string | null;
}

const UnbekannteLeadsDialog: React.FC<UnbekannteLeadsDialogProps> = ({
  open,
  onClose,
  ruleType,
  customer,
  rules,
  existingSources,
  onCreateRule,
  onShowLeadPreview,
}) => {
  const [timeframe, setTimeframe] = useState<PreviewTimeframe>('this_week');
  const [leadStatus, setLeadStatus] = useState<LeadStatus>('all');
  const [loading, setLoading] = useState(false);
  const [distribution, setDistribution] = useState<UnbekannteDistribution | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create rule sub-dialog state
  const [createRule, setCreateRule] = useState<CreateRuleState>({
    open: false,
    column: '',
    value: null,
  });
  const [targetSource, setTargetSource] = useState<string>('');
  const [newSourceName, setNewSourceName] = useState('');

  const analyze = async () => {
    if (!rules) return;
    setLoading(true);
    setError(null);
    try {
      const response = await postData<UnbekannteDistribution>(
        '/api/lead-source-rules/unbekannte-distribution',
        {
          rule_type: ruleType,
          customer,
          rules,
          timeframe,
          lead_status: leadStatus,
        },
      );
      if (response.success && response.data) {
        setDistribution(response.data);
      } else {
        setError(response.error || 'Fehler beim Laden');
      }
    } catch {
      setError('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateRule = (column: string, value: string | null) => {
    setCreateRule({ open: true, column, value });
    setTargetSource('');
    setNewSourceName('');
  };

  const handleConfirmCreateRule = () => {
    const source =
      targetSource === '__new__'
        ? newSourceName.trim().toLowerCase().replace(/\s+/g, '_')
        : targetSource;
    if (!source) return;
    onCreateRule(createRule.column, createRule.value, source);
    setCreateRule({ open: false, column: '', value: null });
  };

  const availableSources = existingSources.filter((s) => s !== 'global' && s !== 'all');

  const formatPercent = (count: number, total: number): string => {
    if (total === 0) return '0.0';
    return ((count / total) * 100).toFixed(1);
  };

  const renderDistributionTable = (column: string, entries: DistributionEntry[]) => {
    if (!distribution) return null;
    if (entries.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          Keine Daten
        </Typography>
      );
    }

    return (
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Wert
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Anzahl
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                %
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                  width: 50,
                }}
                align="center"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  {entry.value === null ? (
                    <Typography variant="body2" fontStyle="italic" color="text.secondary">
                      (leer)
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.value}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{entry.count.toLocaleString('de-DE')}</TableCell>
                <TableCell align="right">
                  {formatPercent(entry.count, distribution.total)} %
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Regel erstellen">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenCreateRule(column, entry.value)}
                    >
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6">
              Unbekannte Leads - {customerNames[customer] || customer}
            </Typography>
            {distribution && (
              <>
                <Chip
                  label={`${distribution.total.toLocaleString('de-DE')} Leads`}
                  color="warning"
                  size="small"
                />
                <Chip label={`${distribution.query_time_ms} ms`} size="small" variant="outlined" />
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Controls */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Zeitraum</InputLabel>
              <Select
                value={timeframe}
                label="Zeitraum"
                onChange={(e) => setTimeframe(e.target.value as PreviewTimeframe)}
              >
                {Object.entries(timeframeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }} disabled={ruleType === 'vnr'}>
              <InputLabel>Lead Status</InputLabel>
              <Select
                value={leadStatus}
                label="Lead Status"
                onChange={(e) => setLeadStatus(e.target.value as LeadStatus)}
              >
                {Object.entries(leadStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
              }
              onClick={analyze}
              disabled={loading || !rules}
            >
              Analysieren
            </Button>

            {distribution && (
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => onShowLeadPreview(timeframe, leadStatus)}
                sx={{ ml: 'auto' }}
              >
                Leads anzeigen
              </Button>
            )}
          </Box>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Distribution sections */}
          {distribution &&
            Object.entries(distribution.distributions).map(([column, entries]) => (
              <Accordion key={column} defaultExpanded={entries.length > 0 && entries.length <= 20}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight="bold">
                      {columnDisplayNames[column] || column}
                    </Typography>
                    <Chip label={`${entries.length} Werte`} size="small" variant="outlined" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {renderDistributionTable(column, entries)}
                </AccordionDetails>
              </Accordion>
            ))}

          {!distribution && !loading && !error && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                Zeitraum und Status auswählen, dann &quot;Analysieren&quot; klicken.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Create Rule Sub-Dialog */}
      <Dialog
        open={createRule.open}
        onClose={() => setCreateRule({ open: false, column: '', value: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Regel erstellen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{columnDisplayNames[createRule.column] || createRule.column}</strong>
            {' = '}
            {createRule.value === null ? <em>(leer)</em> : `"${createRule.value}"`}
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Zu Quelle hinzufügen</InputLabel>
            <Select
              value={targetSource}
              label="Zu Quelle hinzufügen"
              onChange={(e) => setTargetSource(e.target.value)}
            >
              {availableSources.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
              <MenuItem value="__new__">Neue Quelle...</MenuItem>
            </Select>
          </FormControl>

          {targetSource === '__new__' && (
            <TextField
              fullWidth
              label="Name der neuen Quelle"
              value={newSourceName}
              onChange={(e) => setNewSourceName(e.target.value)}
              placeholder="z.B. newsletter, traffic"
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateRule({ open: false, column: '', value: null })}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCreateRule}
            disabled={!targetSource || (targetSource === '__new__' && !newSourceName.trim())}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnbekannteLeadsDialog;
